import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Users,
  Award,
  Target,
  Heart,
  Lightbulb,
  Shield,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { Button } from "../components/ui/button";

const TEAM_IMAGE = "https://images.unsplash.com/photo-1622676614630-a9109126264a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhcmNoaXRlY3QlMjB0ZWFtJTIwbWVldGluZyUyMGRpdmVyc2V8ZW58MHx8fHwxNzY3MDU3NTUzfDA&ixlib=rb-4.1.0&q=85";
const VISION_IMAGE = "https://images.unsplash.com/photo-1761571740780-d9149a88b759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlY28lMjBob3VzZSUyMHNvbGFyJTIwcGFuZWxzJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc2NzA1NzU0N3ww&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Heart,
    title: "Engagement",
    description: "Nous nous engageons pleinement dans chaque projet pour garantir votre satisfaction."
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Nous adoptons les dernières technologies pour des solutions toujours plus performantes."
  },
  {
    icon: Shield,
    title: "Confiance",
    description: "Transparence et honnêteté sont au cœur de notre relation avec nos clients."
  },
  {
    icon: Target,
    title: "Excellence",
    description: "Nous visons l'excellence dans chaque conseil et chaque recommandation."
  }
];

const experts = [
  {
    title: "Diagnostiqueurs immobiliers",
    description: "Experts en performance énergétique, isolation thermique et systèmes de chauffage",
    skills: ["Diagnostic thermique", "Audit énergétique", "Modélisation 3D"]
  },
  {
    title: "Conseillers financiers",
    description: "Spécialistes des aides d'État, subventions et financements de rénovation",
    skills: ["MaPrimeRénov'", "CEE", "Éco-PTZ", "Aides locales"]
  },
  {
    title: "Architectes d'intérieur",
    description: "Designers créatifs pour optimiser votre espace tout en améliorant l'efficacité énergétique",
    skills: ["Design durable", "Optimisation d'espace", "Matériaux écologiques"]
  },
  {
    title: "Électriciens certifiés",
    description: "Professionnels qualifiés pour l'installation de systèmes électriques performants",
    skills: ["Photovoltaïque", "Domotique", "Bornes de recharge"]
  }
];

export default function AboutPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 gradient-hero">
          <img 
            src={TEAM_IMAGE}
            alt="Notre équipe"
            className="w-full h-full object-cover mix-blend-overlay opacity-30"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm font-manrope text-white mb-6">
              <Users size={16} className="text-primary-neon" />
              Notre histoire
            </span>
            <h1 className="font-outfit font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Une équipe passionnée au service de votre confort
            </h1>
            <p className="font-manrope text-lg text-white/80 mb-8">
              Depuis 2018, Eco-Conseil-Habitat accompagne les particuliers dans leurs projets 
              de rénovation énergétique avec expertise et bienveillance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 bg-secondary text-primary rounded-full px-4 py-2 text-sm font-manrope font-semibold mb-6">
                <Target size={16} />
                Notre mission
              </span>
              <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-6">
                Rendre la rénovation énergétique accessible à tous
              </h2>
              <p className="font-manrope text-text-secondary mb-6">
                Notre mission est simple : vous accompagner de A à Z dans votre projet de rénovation 
                énergétique. Nous croyons que chaque foyer mérite de vivre dans un habitat confortable, 
                économique et respectueux de l'environnement.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Accompagnement personnalisé par des experts certifiés",
                  "Optimisation des aides financières disponibles",
                  "Solutions adaptées à votre budget et vos besoins",
                  "Suivi complet de votre projet jusqu'à sa réalisation"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={20} />
                    <span className="font-manrope text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/simulation">
                <Button className="btn-primary" data-testid="btn-about-cta">
                  Démarrer mon projet
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src={VISION_IMAGE}
                alt="Habitat écologique"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-primary text-white p-6 rounded-2xl shadow-xl">
                <div className="font-syne text-4xl font-bold mb-1">+10 000</div>
                <div className="font-manrope text-sm opacity-90">Projets réalisés</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-secondary">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-white text-primary rounded-full px-4 py-2 text-sm font-manrope font-semibold mb-4">
              <Heart size={16} />
              Nos valeurs
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Ce qui nous guide au quotidien
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-lg"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <value.icon className="text-primary" size={28} />
                </div>
                <h3 className="font-outfit font-bold text-lg text-text-primary mb-3">
                  {value.title}
                </h3>
                <p className="font-manrope text-text-muted text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-secondary text-primary rounded-full px-4 py-2 text-sm font-manrope font-semibold mb-4">
              <Award size={16} />
              Notre équipe
            </span>
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-4">
              Des experts pluridisciplinaires
            </h2>
            <p className="font-manrope text-text-secondary">
              Notre force réside dans la diversité de nos compétences. Une équipe complète 
              pour répondre à tous les aspects de votre projet.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {experts.map((expert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="solid-card group"
                data-testid={`expert-card-${index}`}
              >
                <div className="mb-4">
                  <h3 className="font-outfit font-bold text-xl text-text-primary mb-2">
                    {expert.title}
                  </h3>
                  <p className="font-manrope text-sm text-text-muted">
                    {expert.description}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {expert.skills.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-secondary text-primary text-xs font-manrope font-medium rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit font-bold text-3xl md:text-4xl text-text-primary mb-6">
              Prêt à transformer votre habitat ?
            </h2>
            <p className="font-manrope text-text-secondary mb-8 max-w-2xl mx-auto">
              Rejoignez les milliers de familles qui ont fait confiance à Eco-Conseil-Habitat 
              pour leur projet de rénovation énergétique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/simulation">
                <Button className="btn-primary" data-testid="btn-cta-simulation">
                  Commencer ma simulation
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="btn-secondary">
                  Voir nos offres
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
